'use client';

import Link from 'next/link';
import MessageBox from './MessageBox';
import getLoginUrl from '~/clientUtils/getLoginUrl';
import Button from '../Button/Button';
import BackLink from '~public/img/icons/arrow-left-secondary-light.svg';
import { observer } from 'mobx-react';
import { getStore } from '~/mobx/AppStore';
import { JSX } from 'react';
import { FREETEXT_KEYS } from '~/const/freetext';
import Footer from '../Layout/Footer';

type Props = {
    message?: string | JSX.Element | null;
    backLinkText?: string | JSX.Element;
    title?: string | JSX.Element;
    ingress?: string | JSX.Element;
    ingress2?: string | JSX.Element;
    loginBtnText: string;
    error?: boolean;
    hideLoginButton?: boolean;
    hideMessageBox?: boolean;
    backLink: string;
    showCloseTabButton?: boolean;
    closeBtnText?: string;
    errorTitleMessage?: string | JSX.Element;
};

const LoginForm = (props: Props) => {
    const loginUrl = getLoginUrl();

    const {
        message,
        backLinkText,
        title,
        ingress,
        ingress2,
        loginBtnText,
        error,
        hideLoginButton,
        hideMessageBox,
        backLink,
        showCloseTabButton,
        closeBtnText,
        errorTitleMessage,
    } = props;
    const displayMessage: JSX.Element | string = message || '';

    const containerMinHeight = 'max(calc(100vh - 100px), calc(356px - 100px))';
    const containerClasses = 'flex flex-col';
    const mainContentClasses = [
        'container-fluid',
        'box-border',
        'flex w-full flex-1 flex-shrink-0 flex-col',
        'bg-[var(--background-rgb)]',
        'font-sans text-base font-normal leading-6',
    ].join(' ');
    const contentWrapperClasses = ['flex w-full flex-auto flex-col justify-start', 'md:w-7/12 lg:min-w-[48rem]'].join(
        ' '
    );

    return (
        <div className={containerClasses} style={{ minHeight: containerMinHeight }}>
            <div className={mainContentClasses}>
                <div className={contentWrapperClasses}>
                    <Link href={backLink} className="mb-4 inline-flex w-fit items-center text-base">
                        <BackLink className="mr-2" data-test-id="back-link" />
                        {backLinkText}
                    </Link>
                    {errorTitleMessage && <p className="mb-[-20px] self-start text-red-500">{errorTitleMessage}</p>}
                    <h1 className="mb-7 self-start text-3xl font-bold">{title}</h1>

                    <div className="flex w-full flex-col items-start lg:w-3/4">
                        <p className="mb-2 self-start">{ingress}</p>
                        {ingress2 && <p className="mb-2 self-start">{ingress2}</p>}
                        {!hideMessageBox && <MessageBox error={!!error}>{displayMessage}</MessageBox>}
                        {!hideLoginButton && (
                            <div className="w-full text-right">
                                <Link href={loginUrl}>
                                    <Button btnText={loginBtnText} testId="login-button" />
                                </Link>
                            </div>
                        )}
                        {showCloseTabButton && (
                            <div className="w-full text-right">
                                <Button
                                    btnText={closeBtnText || 'Lukk stemmeseddel'}
                                    testId={'close-tab-button'}
                                    onClick={() => window.close()}
                                    variant={'success'}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

const LoginFormWithStore = () => {
    const { ballotStore, freetextStore } = getStore();
    const isLoggedIn = ballotStore.getIsLoggedIn();
    const backLink = ballotStore.coopMainDomain;

    const isStandAdmin = ballotStore.getIsStandAdmin();
    const message = freetextStore.getFreetextValueByKey(FREETEXT_KEYS.login_form_login_info);
    const backLinkText = freetextStore.getFreetextValueByKey(FREETEXT_KEYS.back_button);
    const title = freetextStore.getFreetextValueByKey(FREETEXT_KEYS.login_form_txt);
    const ingress = freetextStore.getFreetextValueByKey(FREETEXT_KEYS.login_form_ingress);
    const ingress2 = freetextStore.getFreetextValueByKey(FREETEXT_KEYS.login_form_freetext, { noFallbackToKey: true });
    const loginBtnText = freetextStore.getFreetextValueByKey(FREETEXT_KEYS.login_form_login_btn);
    const errorTitleMessage = ballotStore.getErrorTitleMessage();
    const loginErrorMessage = ballotStore.getLoginErrorMessage();

    if (!isLoggedIn && !isStandAdmin) {
        return (
            <LoginForm
                message={loginErrorMessage || message}
                error={!!loginErrorMessage}
                backLinkText={backLinkText}
                title={title}
                ingress={ingress}
                ingress2={ingress2}
                loginBtnText={loginBtnText}
                backLink={backLink}
                errorTitleMessage={errorTitleMessage}
            />
        );
    }

    const noActiveElectionError = ballotStore.getNoActiveElectionError();
    const showCloseTabButton = noActiveElectionError.isError && isStandAdmin;

    return (
        <LoginForm
            message={loginErrorMessage || message}
            backLinkText={backLinkText}
            title={title}
            ingress={ingress}
            ingress2={ingress2}
            loginBtnText={loginBtnText}
            backLink={backLink}
            error={!!loginErrorMessage}
            hideLoginButton={true}
            showCloseTabButton={showCloseTabButton}
            closeBtnText={freetextStore.getFreetextValueByKey(FREETEXT_KEYS.btn_close_tab)}
        />
    );
};

export default observer(LoginFormWithStore);
